import { useState, useCallback, useMemo, Fragment, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ExpandMore,
  ChevronRight,
  DragIndicator,
  Add,
  ScaleOutlined,
  Delete,
  Edit,
  Save,
  Undo,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RecipeStatus, RecipeV2Detail, RecipeComponent, StockItemSearchResult } from '../types';
import { applyRoundAndFormat } from '../../../utils';
import useRecipeV2Api from '../hooks/useRecipeV2Api';
import { getComponentMetrics, getRecipeTotalEffectiveWeight, percentOfTotal } from './RecipeComponentsTable';
import RecipeV2EmptyState from './RecipeV2EmptyState';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import { ConfirmationDialog } from '../../../components/shared/dialogs/ConfirmationDialog';
import { Trash01 } from '../../../assets';
import { UserContext } from '../../../components/shared/useUser';

interface IngredientsTabProps {
  recipe: RecipeV2Detail;
  editable: boolean;
  currencyCode?: string;
  onComponentsChange?: (components: RecipeComponent[]) => void;
  onMetadataChange?: (partial: Partial<RecipeV2Detail>) => void;
}

interface TreeRowProps {
  component: RecipeComponent;
  scale: number;
  recipeTotalWeight: number | null;
  parentEffectiveWeight?: number | null;
  depth: number;
  editable: boolean;
  editingId: number | null;
  editDraft: Partial<RecipeComponent> | null;
  formatCurrency: (v: number) => string;
  onStartEdit: (component: RecipeComponent) => void;
  onSaveEdit: () => void;
  onRevertEdit: () => void;
  onEditDraftChange: (field: string, value: number | string | null) => void;
  onRemove: (id: number) => void;
}

/**
 * Non-draggable recursive row for sub-recipe children. Children are rendered as flat
 * sibling <TableRow>s (toggled by `expanded`) rather than a nested <Table> inside a
 * <Collapse>/<TableCell> - the nested-table approach left stray blank rows in the grid.
 */
const TreeRow = ({ component, scale, recipeTotalWeight, parentEffectiveWeight, depth, editable, editingId, editDraft, formatCurrency, onStartEdit, onSaveEdit, onRevertEdit, onEditDraftChange, onRemove }: TreeRowProps) => {
  const { t } = useTranslation('recipeV2');
  const [expanded, setExpanded] = useState(false);
  const hasChildren = component.isRecipe && !!component.children?.length;
  const isEditing = editingId === component.id;
  const metrics = getComponentMetrics(component, scale);
  const weightPercent = percentOfTotal(metrics.effectiveWeight, parentEffectiveWeight ?? recipeTotalWeight);

  return (
    <Fragment>
      <TableRow
        sx={{
          '&:hover': { bgcolor: 'action.hover' },
          bgcolor: depth > 0 ? `grey.${Math.min(50 + depth * 50, 200)}` : undefined,
        }}
      >
        <TableCell sx={{ width: 40, px: 0.5 }} />
        <TableCell sx={{ pl: depth * 3 }}>
          <Stack direction="row"
            alignItems="center"
            spacing={0.5}>
            {hasChildren ? (
              <IconButton size="small"
                onClick={() => setExpanded(!expanded)}>
                {expanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
              </IconButton>
            ) : (
              <Box sx={{ width: 28 }} />
            )}
            <Box>
              <Typography variant="textSM"
                fontWeight={500}>
                {component.componentDescription}
              </Typography>
              <Typography variant="textXS"
                color="text.secondary"
                fontFamily="monospace">
                {component.componentItemNumber}
              </Typography>
            </Box>
            {component.isRecipe && (
              <Chip label={t('build.subRecipe')}
                size="small"
                color="info"
                variant="outlined"
                sx={{ ml: 1 }} />
            )}
          </Stack>
        </TableCell>
        <TableCell align="right">
          {isEditing ? (
            <TextField
              size="small"
              type="number"
              value={editDraft?.quantity ?? component.quantity}
              onChange={(e) => onEditDraftChange('quantity', parseFloat(e.target.value) || 0)}
              sx={{ width: 80 }}
              inputProps={{ step: 0.01, min: 0 }}
            />
          ) : (
            component.quantity.toFixed(3)
          )}
        </TableCell>
        <TableCell align="right">
          {isEditing ? (
            <TextField
              size="small"
              type="number"
              value={editDraft?.weight ?? component.weight}
              onChange={(e) => onEditDraftChange('weight', parseFloat(e.target.value) || 0)}
              sx={{ width: 80 }}
              inputProps={{ step: 1, min: 0 }}
            />
          ) : (
            metrics.effectiveWeight != null ? metrics.effectiveWeight.toFixed(0) : '—'
          )}
        </TableCell>
        <TableCell align="right">
          {isEditing ? (
            <TextField size="small"
              type="number"
              value={editDraft?.volume ?? component.volume}
              onChange={(e) => onEditDraftChange('volume', parseFloat(e.target.value) || 0)}
              sx={{ width: 80 }}
              inputProps={{ step: 0.01, min: 0 }} />
          ) : component.volume.toFixed(3)}
        </TableCell>
        <TableCell align="center">
          {isEditing ? (
            <TextField
              size="small"
              value={editDraft?.uom ?? component.uom}
              onChange={(e) => onEditDraftChange('uom', e.target.value)}
              sx={{ width: 80 }}
            />
          ) : component.uom}
        </TableCell>
        <TableCell align="right">{metrics.effectiveCost != null ? formatCurrency(metrics.effectiveCost) : '—'}</TableCell>
        <TableCell align="right">
          <Stack direction="row"
            alignItems="center"
            justifyContent="flex-end"
            spacing={0.5}>
            <Typography variant="textSM">{weightPercent != null ? `${weightPercent.toFixed(1)}%` : '—'}</Typography>
            <Box
              sx={{
                width: Math.max(weightPercent ?? 0, 4),
                height: 8,
                borderRadius: 1,
                bgcolor: (weightPercent ?? 0) > 30 ? 'error.main' : (weightPercent ?? 0) > 15 ? 'warning.main' : 'success.main',
              }}
            />
          </Stack>
        </TableCell>
        <TableCell align="right">
          {editable && (
            <Stack direction="row"
              spacing={0.5}
              justifyContent="flex-end">
              {isEditing ? (
                <>
                  <IconButton size="small"
                    color="primary"
                    onClick={onSaveEdit}>
                    <Save fontSize="small" />
                  </IconButton>
                  <IconButton size="small"
                    onClick={onRevertEdit}>
                    <Undo fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <IconButton size="small"
                    onClick={() => onStartEdit(component)}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small"
                    color="error"
                    onClick={() => onRemove(component.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </>
              )}
            </Stack>
          )}
        </TableCell>
      </TableRow>
      {hasChildren && expanded && component.children!.map((child) => (
        <TreeRow
          key={child.id}
          component={child}
          scale={metrics.childScale}
          recipeTotalWeight={recipeTotalWeight}
          parentEffectiveWeight={metrics.effectiveWeight}
          depth={depth + 1}
          editable={editable}
          editingId={editingId}
          editDraft={editDraft}
          formatCurrency={formatCurrency}
          onStartEdit={onStartEdit}
          onSaveEdit={onSaveEdit}
          onRevertEdit={onRevertEdit}
          onEditDraftChange={onEditDraftChange}
          onRemove={onRemove}
        />
      ))}
    </Fragment>
  );
};

/** Sortable top-level row (supports drag-and-drop reordering) */
const SortableTreeRow = ({ component, scale, recipeTotalWeight, parentEffectiveWeight, editable, editingId, editDraft, formatCurrency, onStartEdit, onSaveEdit, onRevertEdit, onEditDraftChange, onRemove }: Omit<TreeRowProps, 'depth'>) => {
  const { t } = useTranslation('recipeV2');
  const [expanded, setExpanded] = useState(false);
  const hasChildren = component.isRecipe && !!component.children?.length;
  const isEditing = editingId === component.id;
  const metrics = getComponentMetrics(component, scale);
  const weightPercent = percentOfTotal(metrics.effectiveWeight, parentEffectiveWeight ?? recipeTotalWeight);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: component.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Fragment>
      <TableRow ref={setNodeRef}
        style={style}
        sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
        <TableCell sx={{ width: 40, px: 0.5 }}>
          {editable && (
            <DragIndicator
              {...attributes}
              {...listeners}
              sx={{ color: 'text.disabled', fontSize: 18, cursor: 'grab' }}
            />
          )}
        </TableCell>
        <TableCell>
          <Stack direction="row"
            alignItems="center"
            spacing={0.5}>
            {hasChildren ? (
              <IconButton size="small"
                onClick={() => setExpanded(!expanded)}>
                {expanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
              </IconButton>
            ) : (
              <Box sx={{ width: 28 }} />
            )}
            <Box>
              <Typography variant="textSM"
                fontWeight={500}>
                {component.componentDescription}
              </Typography>
              <Typography variant="textXS"
                color="text.secondary"
                fontFamily="monospace">
                {component.componentItemNumber}
              </Typography>
            </Box>
            {component.isRecipe && (
              <Chip label={t('build.subRecipe')}
                size="small"
                color="info"
                variant="outlined"
                sx={{ ml: 1 }} />
            )}
          </Stack>
        </TableCell>
        <TableCell align="right">
          {isEditing ? (
            <TextField
              size="small"
              type="number"
              value={editDraft?.quantity ?? component.quantity}
              onChange={(e) => onEditDraftChange('quantity', parseFloat(e.target.value) || 0)}
              sx={{ width: 80 }}
              inputProps={{ step: 0.01, min: 0 }}
            />
          ) : (
            component.quantity.toFixed(3)
          )}
        </TableCell>
        <TableCell align="right">
          {isEditing ? (
            <TextField
              size="small"
              type="number"
              value={editDraft?.weight ?? component.weight}
              onChange={(e) => onEditDraftChange('weight', parseFloat(e.target.value) || 0)}
              sx={{ width: 80 }}
              inputProps={{ step: 1, min: 0 }}
            />
          ) : (
            metrics.effectiveWeight != null ? metrics.effectiveWeight.toFixed(0) : '—'
          )}
        </TableCell>
        <TableCell align="right">
          {isEditing ? (
            <TextField size="small"
              type="number"
              value={editDraft?.volume ?? component.volume}
              onChange={(e) => onEditDraftChange('volume', parseFloat(e.target.value) || 0)}
              sx={{ width: 80 }}
              inputProps={{ step: 0.01, min: 0 }} />
          ) : component.volume.toFixed(3)}
        </TableCell>
        <TableCell align="center">
          {isEditing ? (
            <TextField
              size="small"
              value={editDraft?.uom ?? component.uom}
              onChange={(e) => onEditDraftChange('uom', e.target.value)}
              sx={{ width: 80 }}
            />
          ) : component.uom}
        </TableCell>
        <TableCell align="right">{metrics.effectiveCost != null ? formatCurrency(metrics.effectiveCost) : '—'}</TableCell>
        <TableCell align="right">
          <Stack direction="row"
            alignItems="center"
            justifyContent="flex-end"
            spacing={0.5}>
            <Typography variant="textSM">{weightPercent != null ? `${weightPercent.toFixed(1)}%` : '—'}</Typography>
            <Box
              sx={{
                width: Math.max(weightPercent ?? 0, 4),
                height: 8,
                borderRadius: 1,
                bgcolor: (weightPercent ?? 0) > 30 ? 'error.main' : (weightPercent ?? 0) > 15 ? 'warning.main' : 'success.main',
              }}
            />
          </Stack>
        </TableCell>
        <TableCell align="right">
          {editable && (
            <Stack direction="row"
              spacing={0.5}
              justifyContent="flex-end">
              {isEditing ? (
                <>
                  <IconButton size="small"
                    color="primary"
                    onClick={onSaveEdit}>
                    <Save fontSize="small" />
                  </IconButton>
                  <IconButton size="small"
                    onClick={onRevertEdit}>
                    <Undo fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <IconButton size="small"
                    onClick={() => onStartEdit(component)}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small"
                    color="error"
                    onClick={() => onRemove(component.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </>
              )}
            </Stack>
          )}
        </TableCell>
      </TableRow>
      {hasChildren && expanded && component.children!.map((child) => (
        <TreeRow
          key={child.id}
          component={child}
          depth={1}
          scale={metrics.childScale}
          recipeTotalWeight={recipeTotalWeight}
          parentEffectiveWeight={metrics.effectiveWeight}
          editable={editable}
          editingId={editingId}
          editDraft={editDraft}
          formatCurrency={formatCurrency}
          onStartEdit={onStartEdit}
          onSaveEdit={onSaveEdit}
          onRevertEdit={onRevertEdit}
          onEditDraftChange={onEditDraftChange}
          onRemove={onRemove}
        />
      ))}
    </Fragment>
  );
};


const IngredientsTab = ({ recipe, editable, currencyCode, onComponentsChange, onMetadataChange }: IngredientsTabProps) => {
  const { t } = useTranslation('recipeV2');
  const api = useRecipeV2Api();
  const { hasPermissionTo } = useContext(UserContext);
  const [components, setComponents] = useState<RecipeComponent[]>(recipe.components ?? []);
  const recipeTotalWeight = useMemo(() => getRecipeTotalEffectiveWeight(components), [components]);

  // Component row IDs can change when the first edit forks a published version into a draft.
  // Keep local drag/edit state aligned with the latest recipe snapshot supplied by the parent.
  useEffect(() => {
    setComponents(recipe.components ?? []);
  }, [recipe.components, recipe.recipeVersionId]);

  // Scaling state
  const [scaleDialogOpen, setScaleDialogOpen] = useState(false);
  const primaryYield = recipe.yieldPortions ?? undefined;
  const [targetPortions, setTargetPortions] = useState<number>(primaryYield ?? 1);
  const [isScaled, setIsScaled] = useState(false);
  const hasValidYield = !!primaryYield && primaryYield > 0;

  // Add component state
  const [addComponentOpen, setAddComponentOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState<StockItemSearchResult[]>([]);
  const [feedback, setFeedback] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<RecipeComponent | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [productionDialogOpen, setProductionDialogOpen] = useState(false);
  const [productionQuantity, setProductionQuantity] = useState<number>(0);
  const [isProducing, setIsProducing] = useState(false);
  const [productionIdempotencyKey, setProductionIdempotencyKey] = useState<string | null>(null);

  // Edit component state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<Partial<RecipeComponent> | null>(null);

  const formatCurrency = useCallback((value: number) => applyRoundAndFormat(value, currencyCode), [currencyCode]);

  const productionOutputUom = recipe.portionQuantity != null && recipe.portionQuantity > 0 && recipe.portionUom
    ? recipe.portionUom
    : recipe.stockUom ?? '';
  const productionOutputPerExecution = recipe.yieldPortions != null && recipe.yieldPortions > 0
    ? recipe.yieldPortions * (recipe.portionQuantity != null && recipe.portionQuantity > 0 ? recipe.portionQuantity : 1)
    : 0;
  const canManuallyProduce = recipe.status === RecipeStatus.Active
    && recipe.consumptionMode === 'ProducedStock'
    && recipe.productionTrigger === 'Manual'
    && hasPermissionTo(['MadeInStoreWrite']);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = components.findIndex((c) => c.id === active.id);
      const newIndex = components.findIndex((c) => c.id === over.id);
      const reordered = arrayMove(components, oldIndex, newIndex).map((c, i) => ({ ...c, sortOrder: i }));
      try {
        await api.reorderComponents(recipe.id, reordered.map((c) => c.id));
        setComponents(reordered);
        onComponentsChange?.(reordered);
      } catch (error) {
        const message = error instanceof Error
          && error.message.includes('Component order must contain each recipe component exactly once')
          ? t(
            'feedback.componentReorderOutOfSync',
            'The ingredient order could not be saved because the recipe changed. Refresh the recipe and try again.',
          )
          : error instanceof Error
            ? error.message
            : t('feedback.componentReorderError');

        setFeedback({ message, severity: 'error' });
      }
    }
  }, [components, onComponentsChange, api, recipe.id, t]);

  // Edit handlers
  const handleStartEdit = useCallback((component: RecipeComponent) => {
    setEditingId(component.id);
    setEditDraft({
      quantity: component.quantity,
      weight: component.weightSource === 'Authored' ? component.weight : undefined,
      volume: component.volumeSource === 'Authored' ? component.volume : undefined,
      uom: component.uom,
    });
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (editingId === null || !editDraft) return;
    const comp = components.find((c) => c.id === editingId);
    if (comp) {
      try {
        const saved = await api.updateComponent(recipe.id, comp.id, {
          componentKey: comp.componentKey,
          componentProductId: comp.componentProductId,
          componentRole: comp.componentRole,
          quantity: editDraft.quantity ?? comp.quantity,
          weight: editDraft.weight,
          volume: editDraft.volume,
          uom: editDraft.uom ?? comp.uom,
          sortOrder: comp.sortOrder,
        });
        const updated = components.map((item) => item.id === editingId ? saved : item);
        setComponents(updated);
        onComponentsChange?.(updated);
        setFeedback({ message: t('feedback.componentUpdated'), severity: 'success' });
      } catch (error) {
        setFeedback({ message: error instanceof Error ? error.message : t('feedback.componentUpdateError'), severity: 'error' });
        return;
      }
    }
    setEditingId(null);
    setEditDraft(null);
  }, [editingId, editDraft, onComponentsChange, api, recipe.id, components]);

  const handleRevertEdit = useCallback(() => {
    setEditingId(null);
    setEditDraft(null);
  }, []);

  const handleEditDraftChange = useCallback((field: string, value: number | string | null) => {
    // Quantity/UOM is the authored source of truth. Weight and volume are independent
    // physical facts and must not be reverse-calculated in the browser. The backend
    // resolves any derived measurements through the governed UOM conversion service.
    setEditDraft((current) => ({ ...(current ?? {}), [field]: value }));
  }, []);

  // Remove handler - the delete icon only opens the confirmation; the API call runs in confirmRemoveComponent.
  const handleRequestRemove = useCallback((id: number) => {
    const component = components.find((c) => c.id === id);
    if (component) setDeleteCandidate(component);
  }, [components]);

  const confirmRemoveComponent = useCallback(async () => {
    if (!deleteCandidate) return;
    setIsRemoving(true);
    try {
      await api.removeComponent(recipe.id, deleteCandidate.id);
      const updated = components.filter((c) => c.id !== deleteCandidate.id).map((c, i) => ({ ...c, sortOrder: i }));
      setComponents(updated);
      onComponentsChange?.(updated);
      setFeedback({ message: t('feedback.componentRemoved'), severity: 'success' });
      setDeleteCandidate(null);
    } catch (error) {
      setFeedback({ message: error instanceof Error ? error.message : t('feedback.componentRemoveError'), severity: 'error' });
    } finally { setIsRemoving(false); }
  }, [components, deleteCandidate, onComponentsChange, api, recipe.id, t]);

  // Scaling
  // Scale previews a different yield only - ingredient quantities stay at the base recipe
  // amount. Preview-only: nothing is written back to the recipe or its components.
  const handleApplyScale = () => {
    if (!hasValidYield) {
      setFeedback({ message: 'Set and save a positive yield portions value before scaling this recipe.', severity: 'error' });
      return;
    }
    if (!Number.isFinite(targetPortions) || targetPortions <= 0) {
      setFeedback({ message: 'Enter a positive target yield quantity.', severity: 'error' });
      return;
    }
    setIsScaled(true);
    setScaleDialogOpen(false);
    setFeedback({ message: t('build.scaleApplied', { target: targetPortions }), severity: 'success' });
  };

  const handleResetScale = () => {
    setIsScaled(false);
    setTargetPortions(primaryYield ?? 1);
    setFeedback({ message: t('build.scaleResetSuccess'), severity: 'success' });
  };

  const openProductionDialog = () => {
    setProductionQuantity(productionOutputPerExecution);
    setProductionIdempotencyKey(
      globalThis.crypto?.randomUUID?.()
        ?? `recipe-production-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`,
    );
    setProductionDialogOpen(true);
  };

  const handleProduceStock = async () => {
    if (!Number.isFinite(productionQuantity) || productionQuantity <= 0) {
      setFeedback({ message: t('batch.invalidQuantity', 'Enter a positive production quantity.'), severity: 'error' });
      return;
    }

    setIsProducing(true);
    try {
      const result = await api.convertToStock(recipe.id, {
        quantity: productionQuantity,
        uom: productionOutputUom,
        idempotencyKey: productionIdempotencyKey ?? undefined,
      });
      setFeedback({
        message: t('batch.convertSuccess', {
          quantity: result.quantity,
          uom: result.uom,
          itemNumber: recipe.itemNumber,
        }),
        severity: 'success',
      });
      setProductionDialogOpen(false);
      setProductionIdempotencyKey(null);
    } catch (error) {
      setFeedback({
        message: error instanceof Error ? error.message : t('batch.convertError', 'Failed to produce stock.'),
        severity: 'error',
      });
    } finally {
      setIsProducing(false);
    }
  };

  // Add component (stock items and recipes/sub-recipes are both searchable and addable here)
  const handleSearchItems = async (value: string) => {
    if (value.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const results = await api.searchStockItems(value);
      setSearchResults(results.filter((r) =>
        !components.some((c) => c.componentProductId === r.id)
        && !(r.isRecipe && r.id === recipe.id),
      ));
    } catch (error) {
      setSearchResults([]);
      setFeedback({ message: error instanceof Error ? error.message : t('feedback.componentSearchError'), severity: 'error' });
    }
  };

  const handleAddItem = async (item: StockItemSearchResult | null) => {
    if (!item) return;
    try {
      await api.addComponent(recipe.id, {
        componentProductId: item.id,
        componentItemNumber: item.itemNumber,
        componentDescription: item.description,
        quantity: 1,
        weight: null,
        volume: null,
        uom: item.uom,
        componentRole: item.isRecipe ? 'NestedRecipe' : 'Ingredient',
      });
      const refreshed = await api.getRecipeDetail(recipe.id);
      const updated = refreshed.components ?? [];
      setComponents(updated);
      onComponentsChange?.(updated);
      setFeedback({ message: t('feedback.componentAdded'), severity: 'success' });
    } catch (error) {
      setFeedback({ message: error instanceof Error ? error.message : t('feedback.componentAddError'), severity: 'error' });
      return;
    }
    setSearchInput('');
    setSearchResults([]);
    setAddComponentOpen(false);
  };

  return (
    <Stack spacing={2}>
      {/* Recipe meta + action bar - use a responsive grid so labels do not get cramped.
          Normal recipes use two rows on md+; ProducedStock adds one operational row. */}
      <Grid container
        spacing={2}
        alignItems="flex-start"
        sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
        <Grid item
          xs={12}
          sm={6}
          md={2}>
          <TextField
            label="Yield portions"
            type="number"
            value={(isScaled ? targetPortions : primaryYield) ?? ''}
            onChange={(e) => onMetadataChange?.({
              yieldPortions: e.target.value === '' ? null : Number(e.target.value),
            })}
            disabled={!editable || isScaled}
            inputProps={{ min: 0.01, step: 'any' }}
            fullWidth
          />
        </Grid>

        <Grid item
          xs={12}
          sm={6}
          md={2}>
          <TextField
            label="Portion quantity"
            type="number"
            value={recipe.portionQuantity ?? ''}
            onChange={(e) => onMetadataChange?.(
              e.target.value === ''
                ? { portionQuantity: null, portionUom: null }
                : { portionQuantity: Number(e.target.value) },
            )}
            disabled={!editable}
            inputProps={{ min: 0.01, step: 'any' }}
            fullWidth
          />
        </Grid>

        <Grid item
          xs={12}
          sm={6}
          md={2}>
          <TextField
            label="Portion UOM"
            value={recipe.portionUom ?? ''}
            onChange={(e) => onMetadataChange?.({
              portionUom: e.target.value.trim() === '' ? null : e.target.value,
            })}
            disabled={!editable}
            fullWidth
          />
        </Grid>

        <Grid item
          xs={12}
          sm={6}
          md={3}>
          <TextField
            label="Expected yield loss %"
            type="number"
            value={recipe.expectedYieldLossPercent ?? ''}
            onChange={(e) => onMetadataChange?.({
              expectedYieldLossPercent: e.target.value === '' ? null : Number(e.target.value),
            })}
            disabled={!editable}
            inputProps={{ min: 0, max: 100, step: 'any' }}
            fullWidth
          />
        </Grid>

        <Grid item
          xs={12}
          sm={6}
          md={3}>
          <TextField
            label="Consumption mode"
            select
            value={recipe.consumptionMode ?? 'PerPortion'}
            onChange={(e) => {
              const consumptionMode = e.target.value as 'PerPortion' | 'WholeYield' | 'ProducedStock';
              onMetadataChange?.({
                consumptionMode,
                ...(consumptionMode === 'ProducedStock'
                  ? {}
                  : {
                    productionTrigger: null,
                    storageMethod: null,
                    shelfLifeValue: null,
                    shelfLifeUnit: null,
                  }),
              });
            }}
            disabled={!editable}
            fullWidth
          >
            <MenuItem value="PerPortion">Per portion</MenuItem>
            <MenuItem value="WholeYield">Whole yield</MenuItem>
            <MenuItem value="ProducedStock">Produced stock</MenuItem>
          </TextField>
        </Grid>

        {recipe.consumptionMode === 'ProducedStock' && (
          <>
            <Grid item
              xs={12}
              sm={6}
              md={3}>
              <TextField
                label="Production trigger"
                select
                value={recipe.productionTrigger ?? ''}
                onChange={(e) => onMetadataChange?.({
                  productionTrigger: e.target.value === ''
                    ? null
                    : e.target.value as 'Automatic' | 'Manual',
                })}
                disabled={!editable}
                fullWidth
              >
                <MenuItem value="">Not set</MenuItem>
                <MenuItem value="Automatic">Automatic</MenuItem>
                <MenuItem value="Manual">Manual</MenuItem>
              </TextField>
            </Grid>

            <Grid item
              xs={12}
              sm={6}
              md={3}>
              <TextField
                label="Storage method"
                value={recipe.storageMethod ?? ''}
                onChange={(e) => onMetadataChange?.({
                  storageMethod: e.target.value.trim() === '' ? null : e.target.value,
                })}
                disabled={!editable}
                fullWidth
              />
            </Grid>

            <Grid item
              xs={12}
              sm={6}
              md={3}>
              <TextField
                label="Shelf life"
                type="number"
                value={recipe.shelfLifeValue ?? ''}
                onChange={(e) => onMetadataChange?.({
                  shelfLifeValue: e.target.value === '' ? null : Number(e.target.value),
                })}
                disabled={!editable}
                inputProps={{ min: 0.01, step: 'any' }}
                fullWidth
              />
            </Grid>

            <Grid item
              xs={12}
              sm={6}
              md={3}>
              <TextField
                label="Shelf-life unit"
                value={recipe.shelfLifeUnit ?? ''}
                onChange={(e) => onMetadataChange?.({
                  shelfLifeUnit: e.target.value.trim() === '' ? null : e.target.value,
                })}
                disabled={!editable}
                fullWidth
              />
            </Grid>
          </>
        )}

        <Grid item
          xs={12}
          sm={6}
          md={2}>
          <TextField
            label={t('build.totalCost')}
            value={recipe.costIsComplete && recipe.totalCost != null ? formatCurrency(recipe.totalCost) : '—'}
            disabled
            fullWidth
          />
        </Grid>

        <Grid item
          xs={12}
          sm={6}
          md={2}>
          <TextField
            label={t('build.costPerPortion')}
            value={recipe.costIsComplete && isScaled && recipe.totalCost != null && targetPortions > 0
              ? formatCurrency(recipe.totalCost / targetPortions)
              : recipe.costIsComplete && recipe.costPerPortion != null ? applyRoundAndFormat(recipe.costPerPortion, currencyCode, 4) : '—'}
            disabled
            fullWidth
          />
        </Grid>

        {editable && (
          <Grid item
            xs={12}
            md={8}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <Stack direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              justifyContent={{ xs: 'stretch', md: 'flex-end' }}
              sx={{ width: '100%', '& > button': { flex: { xs: '1 1 auto', sm: '0 0 auto' } } }}>
              <Button size="md"
                startIcon={<ScaleOutlined />}
                variant="secondary"
                onClick={() => {
                  // primaryYield can have changed (e.g. Save) since this component mounted.
                  if (!isScaled) setTargetPortions(primaryYield ?? 1);
                  setScaleDialogOpen(true);
                }}
                disabled={!hasValidYield}>
                {t('build.scale')}
              </Button>
              {canManuallyProduce && (
                <Button
                  size="md"
                  variant="secondary"
                  onClick={openProductionDialog}
                  disabled={productionOutputPerExecution <= 0 || !productionOutputUom}
                >
                  {t('batch.convertToStock')}
                </Button>
              )}
              <Button size="md"
                startIcon={<Add />}
                variant="primary"
                onClick={() => setAddComponentOpen(true)}>
                {t('build.addComponent')}
              </Button>
            </Stack>
          </Grid>
        )}
      </Grid>

      <Dialog
        open={productionDialogOpen}
        onClose={() => !isProducing && setProductionDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('batch.convertToStock')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}
            sx={{ pt: 1 }}>
            <Typography variant="textSM"
              color="text.secondary">
              {t('batch.convertPrompt')}
            </Typography>
            <TextField
              label={t('batch.quantity')}
              type="number"
              value={productionQuantity || ''}
              onChange={(event) => setProductionQuantity(Number(event.target.value))}
              inputProps={{
                min: productionOutputPerExecution || undefined,
                step: productionOutputPerExecution || 'any',
              }}
              helperText={productionOutputPerExecution > 0
                ? t('batch.wholeExecutionHint', {
                  quantity: productionOutputPerExecution,
                  uom: productionOutputUom,
                })
                : undefined}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            onClick={() => setProductionDialogOpen(false)}
            disabled={isProducing}
          >
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleProduceStock}
            disabled={isProducing || productionQuantity <= 0}
          >
            {t('batch.convertConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Scaled indicator */}
      {isScaled && (
        <Stack direction="row"
          spacing={1}
          alignItems="center">
          <Chip
            label={t('build.scaledFrom', { original: primaryYield, target: targetPortions })}
            size="small"
            color="info"
          />
          <Button size="md"
            variant="secondary"
            onClick={handleResetScale}>
            {t('build.scaleReset')}
          </Button>
        </Stack>
      )}

      {/* Component tree with DnD */}
      <DndContext sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}>
        <SortableContext items={components.map((c) => c.id)}
          strategy={verticalListSortingStrategy}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small"
              sx={{ minWidth: 850 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell>{t('ingredient')}</TableCell>
                  <TableCell align="right">{t('build.qty')}</TableCell>
                  <TableCell align="right">{t('build.weight')}</TableCell>
                  <TableCell align="right">{t('build.volume')}</TableCell>
                  <TableCell align="center">{t('build.uom')}</TableCell>
                  <TableCell align="right">{t('cost')}</TableCell>
                  <TableCell align="right">% of Total Weight</TableCell>
                  {editable && <TableCell align="right">{t('actions.title')}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {components.length === 0 ? (
                  <RecipeV2EmptyState colSpan={editable ? 9 : 8}>
                    {t('build.noComponents', 'No ingredients added yet.')}
                  </RecipeV2EmptyState>
                ) : components.map((component) => (
                  <SortableTreeRow
                    key={component.id}
                    component={component}
                    scale={1}
                    recipeTotalWeight={recipeTotalWeight}
                    editable={editable}
                    editingId={editingId}
                    editDraft={editingId === component.id ? editDraft : null}
                    formatCurrency={formatCurrency}
                    onStartEdit={handleStartEdit}
                    onSaveEdit={handleSaveEdit}
                    onRevertEdit={handleRevertEdit}
                    onEditDraftChange={handleEditDraftChange}
                    onRemove={handleRequestRemove}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SortableContext>
      </DndContext>

      {/* Scale dialog */}
      <Dialog open={scaleDialogOpen}
        onClose={() => setScaleDialogOpen(false)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>{t('build.scaleTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>{t('build.scalePrompt')}</Typography>
          {!hasValidYield && (
            <Typography color="error"
              sx={{ mb: 2 }}>
              Set and save a positive yield portions value before scaling this recipe.
            </Typography>
          )}
          <TextField
            type="number"
            fullWidth
            label={t('build.portions')}
            value={targetPortions}
            onChange={(e) => setTargetPortions(Number(e.target.value) || 1)}
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScaleDialogOpen(false)}
            variant="secondary">{t('actions.cancel')}</Button>
          <Button onClick={handleApplyScale}
            disabled={!hasValidYield || !Number.isFinite(targetPortions) || targetPortions <= 0}
            variant="primary">{t('build.scaleApply')}</Button>
        </DialogActions>
      </Dialog>

      {/* Add component dialog */}
      <Dialog open={addComponentOpen}
        onClose={() => setAddComponentOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{t('build.addComponent')}</DialogTitle>
        <DialogContent>
          <Typography variant="textSM"
            color="text.secondary"
            sx={{ mb: 1 }}>
            {t('build.addComponentHelp')}
          </Typography>
          <Autocomplete
            options={searchResults}
            getOptionLabel={(o) => `${o.itemNumber} — ${o.description}`}
            renderOption={(props, o) => (
              <Box component="li"
                {...props}
                key={`${o.isRecipe ? 'recipe' : 'stock'}-${o.id}`}>
                <Stack direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: '100%' }}>
                  <span>{o.itemNumber} — {o.description}</span>
                  {o.isRecipe && (
                    <Chip label={t('build.subRecipe')}
                      size="small"
                      color="info"
                      variant="outlined" />
                  )}
                </Stack>
              </Box>
            )}
            inputValue={searchInput}
            onInputChange={(_, value) => {
              setSearchInput(value);
              handleSearchItems(value);
            }}
            onChange={(_, value) => handleAddItem(value)}
            renderInput={(params) => (
              <TextField {...params}
                label={t('build.searchComponents')}
                sx={{ mt: 1 }}
              />
            )}
            noOptionsText={searchInput.length < 2 ? 'Type to search...' : 'No results'}
            value={null}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddComponentOpen(false)}
            variant="secondary">{t('actions.cancel')}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={Boolean(deleteCandidate)}
        icon={<Trash01 />}
        variant="bad"
        title={t('feedback.removeComponentConfirmTitle')}
        message={t('feedback.removeComponentConfirmMessage')}
        confirmButtonText={isRemoving ? t('actions.deleting') : t('actions.delete')}
        cancelButtonText={t('actions.cancel')}
        isProcessing={isRemoving}
        onConfirm={confirmRemoveComponent}
        onCancel={() => setDeleteCandidate(null)}
      />
      {feedback && (
        <MessagesSnackbar
          open={Boolean(feedback)}
          onClose={() => setFeedback(null)}
          message={feedback.message}
          severity={feedback.severity}
          duration={4000}
        />
      )}
    </Stack>
  );
};

export default IngredientsTab;
